import axios from "axios";
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";
import { store } from "../store/store";
import { LOGOUT_ACTION } from "../store/actions/types/AuthTypes";
import { queryClient } from "./queryClient";

const baseUrl = `${import.meta.env.VITE_BACKEND_DOMAIN}/api/`;

const axiosInstance = axios.create({
  baseURL: baseUrl,
  withCredentials: true,
  withXSRFToken: true
});

// Set by App.js on mount so the interceptor can navigate without a hard reload.
let _navigate = null;
export function setNavigate(fn) {
  _navigate = fn;
}

// Guards against concurrent errors (401/403) each triggering their own cleanup.
let isHandlingLogout = false;

function handleUnauthorized() {
  if (isHandlingLogout) return;
  isHandlingLogout = true;

  // Best-effort server-side session kill. Uses fetch (not axiosInstance) to
  // avoid re-entering this interceptor if the logout call itself returns 401/403.
  // Reads the XSRF-TOKEN cookie so the POST passes Sanctum's CSRF check when the
  // session is still present; omits it gracefully when the cookie is absent.
  const xsrfToken = (document.cookie.match(/(?:^|; )XSRF-TOKEN=([^;]*)/) ?? [])[1];
  const logoutHeaders = { 'Accept': 'application/json', 'Content-Type': 'application/json' };
  if (xsrfToken) logoutHeaders['X-XSRF-TOKEN'] = decodeURIComponent(xsrfToken);

  fetch(`${import.meta.env.VITE_BACKEND_DOMAIN}/api/logout`, {
    method: 'POST',
    credentials: 'include',
    headers: logoutHeaders,
  }).catch(() => {
    // Session may already be gone — that's fine, we're clearing client state anyway.
  }).finally(() => {
    store.dispatch({ type: LOGOUT_ACTION });
    queryClient.clear();

    isHandlingLogout = false;

    if (_navigate) {
      _navigate('/login', { replace: true });
    } else {
      window.location.href = '/login';
    }
  });
}

// Messages from EnsureUserAndPartnerAreActive that signal mid-session revocation.
export const ACCOUNT_REVOCATION_MESSAGES = [
  'Your account has been suspended',
  'Your account has been deactivated',
  'Your organisation account is currently inactive',
];

function handleAccountRevoked(errorMessage) {
  if (isHandlingLogout) return;
  isHandlingLogout = true;

  store.dispatch({ type: LOGOUT_ACTION });
  queryClient.clear();
  toast.error(errorMessage);

  isHandlingLogout = false;

  if (_navigate) {
    _navigate('/login', { replace: true });
  } else {
    window.location.href = '/login';
  }
}

axiosInstance.interceptors.request.use((config) => {
  config.headers["Accept"] = `application/json`;
  return config;
});

// HTTP statuses whose body text is written by Laravel or the infrastructure
// rather than by a controller: method not allowed and any 5xx. Their raw
// messages ("The DELETE method is not supported for route ...", "Server
// Error", "SQLSTATE[23000] ...") must never reach a user, so we blank the
// body message here and let each caller's own fallback copy render instead.
// Real message stays on the console for debugging. 404 is not in this set
// because controllers curate their own "not found" messages there.
const FRAMEWORK_STATUS_CODES = new Set([405, 500, 502, 503, 504]);

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const requestUrl = error.config ? error.config.url : '';

    if (error.response && FRAMEWORK_STATUS_CODES.has(error.response.status)) {
      const rawMessage = error.response.data?.message;
      if (rawMessage) {
        // captureException so Sentry groups events by the axios call site
        // rather than by the interpolated URL string, and so a full request
        // and response context comes along automatically. The tag lets us
        // filter the "framework message we suppressed from users" subset
        // in the Sentry UI; the extra keeps the raw framework text visible
        // on the event without smearing it across every issue's title.
        Sentry.captureException(error, {
          level: 'warning',
          tags: {
            framework_status: true,
            http_status: error.response.status,
          },
          extra: {
            url: requestUrl,
            raw_message: rawMessage,
          },
        });
        if (error.response.data && typeof error.response.data === 'object') {
          error.response.data.message = undefined;
        }
      }
    }

    if (error.response && error.response.status === 401) {
      if (requestUrl.includes("login")) {
        // Wrong email/password — let the Login page handle this.
        return Promise.reject(error);
      }

      if (error.response.data?.message === 'Please verify your email before you can continue') {
        if (_navigate) {
          _navigate('/verify-email', { replace: true });
        } else {
          window.location.href = "/verify-email";
        }
        return Promise.reject(error);
      }

      handleUnauthorized();
    }

    if (error.response && error.response.status === 403) {
      if (!requestUrl.includes("login")) {
        const errorMessage = error.response.data?.error ?? '';
        const isRevocation = ACCOUNT_REVOCATION_MESSAGES.some(msg => errorMessage.startsWith(msg));
        if (isRevocation) {
          handleAccountRevoked(errorMessage);
        }
      }
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
